import { omit, pick } from 'lodash-es';
import { useThemeContext } from 'providers/Theme';
import React, { memo } from 'react';
import Space, { SpaceProps } from 'styled-space';

import { StyledIconProps, Svg } from './styles';
import { IconProps, defaultStyleImportance } from './types';

const spacePropKeys = [
  'm',
  'mt',
  'mr',
  'mb',
  'ml',
  'mx',
  'my',
  'p',
  'pt',
  'pr',
  'pb',
  'pl',
  'px',
  'py',
];

const getAdditionalProps = <T extends SVGSVGElement>({
  descriptionId,
  interactive = false,
  semantic = false,
  ...rest
}: Omit<IconProps<T>, 'href' | 'name' | 'ref'>) => {
  const props = {
    ...(semantic && !interactive ? { role: 'img' } : {}),
    ...(!semantic && !interactive
      ? { 'aria-hidden': true, role: 'presentation' }
      : {}),
    ...(interactive ? { role: 'img', tabIndex: 0 } : {}),
    ...(descriptionId ? { 'aria-describedby': descriptionId } : {}),
  };

  return { ...props, ...rest };
};

/**
 * ## IconBase: Accesible SVG Icon Component
 *
 * ### Purpose:
 *
 * Is responsible for basic rendering logic, and accessibility features.
 *
 * Does not do any styling / state logic.
 *
 * ### Implementation Details:
 *
 * SVG implementation relies on an external svg source file combined with
 * the SVG <use> element.  But is not coupled to any specific external source.
 * [MDN Web Docs - use](https://developer.mozilla.org/en-US/docs/Web/SVG/Element/use "MDN Docs use Element")
 *
 * ### Accesibility Considerations:
 *
 * See props docs for more detail on accessibility features.
 *
 * [Accessibly SVG Approach Comparison](https://www.deque.com/blog/creating-accessible-svgs "Accessibly SVG Approach Comparison")
 *
 * [SVG Accessibility Tips](https://www.sitepoint.com/tips-accessible-svg/ "SVG Accessibility Tips")
 * [Accessible SVG Icon Buttons](https://www.sarasoueidan.com/blog/accessible-icon-buttons/ "Accessible SVG Icon Buttons")
 */
const Icon = <T extends SVGSVGElement>({
  children,
  className,
  color,
  description,
  descriptionId,
  height = '16px',
  href,
  forwardRef,
  name,
  styleImportance = defaultStyleImportance,
  themeColor = 'darkBlue',
  title,
  width = '16px',
  minWidth,
  rotate,
  ...props
}: IconProps<T> & StyledIconProps & SpaceProps & { rotate?: boolean }) => {
  const spaceProps = pick(props, spacePropKeys);
  const restProps = omit(props, ...spacePropKeys);

  const { theme } = useThemeContext();
  const finalColor = themeColor && !color ? theme.colors[themeColor] : color;
  const finalHref = name ? `#${name}` : href;

  return (
    <Space {...spaceProps}>
      <Svg
        aria-describedby={descriptionId}
        className={className}
        color={finalColor}
        focusable="false"
        height={height}
        width={width}
        minWidth={minWidth}
        $styleImportance={styleImportance}
        $rotate={rotate}
        {...{ ...getAdditionalProps(restProps) }}
        ref={forwardRef}
      >
        {title && <title>{title}</title>}
        {description && descriptionId && (
          <desc id={descriptionId}>{description}</desc>
        )}
        {(href || name) && <use href={finalHref} />}
        {children}
      </Svg>
    </Space>
  );
};

export default memo(Icon);
